import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  isLive,
  rememberSearch,
  resultsForTab,
  searchCatalog,
  searchTabs,
  type Channel,
  type Program,
  type SearchTab,
  watchStateOf,
  type VodItem,
  type WatchProgress,
} from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { useSpatialFocus } from '../useSpatialFocus.js';
import { OnScreenKeyboard, type KeyboardKey } from '../components/OnScreenKeyboard.js';
import { PosterTile } from '../components/PosterTile.js';
import { channelColorOf } from '../channelColors.js';

/**
 * Cerca (03-SCHERMATE-TV §3.9): canali, programmi, film e serie in un colpo solo.
 *
 * ## Il fuoco lo muove la navigazione spaziale, non un cursore
 *
 * Le altre schermate hanno un cursore che è un numero, perché sono elenchi o griglie regolari. Qui
 * no: a sinistra c'è una tastiera a griglia, a destra sezioni di forme diverse — schede, card evento,
 * locandine, righe di canale — e il numero di risultati cambia a ogni lettera. Un cursore numerico
 * dovrebbe sapere *dove* si trova ogni cosa, e andrebbe ricalcolato a ogni tasto premuto.
 *
 * `useSpatialFocus` fa la cosa giusta: guarda **dove sono gli elementi sullo schermo** e va nella
 * direzione premuta. È lo stesso modello della schermata d'accesso, che ha lo stesso problema.
 *
 * ## Quel che si cerca è quel che si ha
 *
 * Nessuna chiamata: si cerca nel catalogo già in locale (vedi `search.ts`). I programmi sono quelli
 * della finestra caricata dalla guida, non tutta la settimana — e chi disegna lo **dice**, invece di
 * far credere che quello sia tutto il palinsesto.
 */

export interface SearchScreenProps {
  readonly focusPort: FocusPort;
  readonly channels: ReadonlyArray<Channel>;
  readonly programs: ReadonlyArray<Program>;
  readonly vod: ReadonlyArray<VodItem>;
  readonly progress: WatchProgress;
  readonly nowMs: number;
  /** Le ricerche recenti, dalla memoria dell'apparecchio. */
  readonly recent: ReadonlyArray<string>;
  readonly onRecentChange: (recent: ReadonlyArray<string>) => void;
  readonly onOpenChannel: (channelId: string) => void;
  readonly onOpenVod: (item: VodItem) => void;
  /** OK su un programma: in onda porta al canale, futuro accende il promemoria. */
  readonly onToggleReminder: (channelId: string, startMs: number) => void;
  readonly reminderIds: ReadonlyArray<string>;
  readonly onExit: () => void;
}

/** La colonna della tastiera: 10 tasti per riga come nel prototipo, più il respiro. */
const SEARCH_KEYBOARD_WIDTH = 720;

const LETTERS = 'abcdefghijklmnopqrstuvwxyz'.split('');
const DIGITS = '0123456789'.split('');

/**
 * I tasti della ricerca: lettere, cifre e spazio.
 *
 * Niente maiuscole e niente simboli: il confronto è già senza accenti né maiuscole
 * (`normalizeForSearch`), quindi una fila di lettere maiuscole sarebbe **trentasei tasti in più che
 * fanno esattamente quello che fanno quelli sotto**. Cancella e svuota stanno fuori dalla griglia,
 * perché sono parole e vanno tradotte.
 */
export const SEARCH_KEYS: ReadonlyArray<KeyboardKey> = [
  ...LETTERS.map((letter) => ({ label: letter, text: letter, span: 1 })),
  ...DIGITS.map((digit) => ({ label: digit, text: digit, span: 1 })),
];

export function SearchScreen({
  focusPort,
  channels,
  programs,
  vod,
  progress,
  nowMs,
  recent,
  onRecentChange,
  onOpenChannel,
  onOpenVod,
  onToggleReminder,
  reminderIds,
  onExit,
}: SearchScreenProps): ReactNode {
  const translator = useTranslator();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<SearchTab>('all');

  const frameRef = useRef<HTMLDivElement | null>(null);
  const handlers = useRef({ onExit, onRecentChange, onOpenChannel, onOpenVod, onToggleReminder });
  useEffect(() => {
    handlers.current = { onExit, onRecentChange, onOpenChannel, onOpenVod, onToggleReminder };
  }, [onExit, onRecentChange, onOpenChannel, onOpenVod, onToggleReminder]);

  const type = useCallback((text: string) => setQuery((at) => (at + text).slice(0, 40)), []);
  const erase = useCallback(() => setQuery((at) => at.slice(0, -1)), []);
  const clear = useCallback(() => setQuery(''), []);

  useSpatialFocus(focusPort, frameRef, {
    onCharacter: type,
    onErase: erase,
    onBack: () => {
      handlers.current.onExit();
      return true;
    },
  });

  /*
    I canali per identificativo: le card dei programmi devono dire su quale canale vanno in onda, e
    cercare dentro l'elenco per ogni risultato sarebbe un giro completo per riga.
  */
  const channelsById = useMemo(() => {
    const map = new Map<string, Channel>();
    for (const channel of channels) map.set(channel.id, channel);
    return map;
  }, [channels]);

  const all = useMemo(
    () => searchCatalog({ query, channels, programs, vod, nowMs }),
    [query, channels, programs, vod, nowMs],
  );
  const tabs = useMemo(() => searchTabs(all), [all]);

  /*
    La scheda scelta torna a «Tutti» quando **smette di esistere**.

    Si cerca «te», si passa alla scheda Canali, si aggiunge una lettera e di canali non ce n'è più
    nessuno: la scheda selezionata punterebbe a un elenco che non c'è, e la schermata direbbe
    «nessun risultato» mentre a fianco ci sono tre schede piene. È lo stesso difetto della categoria
    che sparisce sotto il cursore nella guida.
  */
  const activeTab: SearchTab = tabs.some((entry) => entry.tab === tab) ? tab : (tabs[0]?.tab ?? 'all');
  const shown = useMemo(() => resultsForTab(all, activeTab), [all, activeTab]);

  /** Si ricorda la ricerca quando si apre qualcosa, non a ogni lettera digitata. */
  const remember = useCallback(() => {
    handlers.current.onRecentChange(rememberSearch(recent, query));
  }, [recent, query]);

  const openChannel = useCallback(
    (channelId: string) => {
      remember();
      handlers.current.onOpenChannel(channelId);
    },
    [remember],
  );

  const openVod = useCallback(
    (item: VodItem) => {
      remember();
      handlers.current.onOpenVod(item);
    },
    [remember],
  );

  const activateProgram = useCallback(
    (item: Program) => {
      if (isLive(item, nowMs)) {
        openChannel(item.channelId);
        return;
      }
      handlers.current.onToggleReminder(item.channelId, item.startMs);
    },
    [nowMs, openChannel],
  );

  const searching = all.total > 0 || query.trim().length > 0;

  return (
    <div
      ref={frameRef}
      data-search="1"
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        gap: 48,
        padding: '56px 72px 0 60px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* La colonna di sinistra: campo e tastiera. Larghezza fissa, come nelle impostazioni. */}
      <div
        style={{
          flex: 'none',
          width: SEARCH_KEYBOARD_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          paddingBottom: 60,
        }}
      >
        <h1 style={{ font: `800 56px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>
          {translator.t('search.title')}
        </h1>

        <div
          data-search-field="1"
          style={{
            minHeight: 78,
            borderRadius: 14,
            background: colors.surface,
            border: `2px solid ${query.length > 0 ? colors.accent : colors.border}`,
            padding: '16px 24px',
            font: `600 30px ${FONT_STACK}`,
            color: query.length > 0 ? colors.text : colors.textFaint,
            display: 'flex',
            alignItems: 'center',
            wordBreak: 'break-word',
          }}
        >
          {query.length > 0 ? query : translator.t('search.placeholder')}
        </div>

        <OnScreenKeyboard keys={SEARCH_KEYS} onType={type} />

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" data-f="1" onClick={() => type(' ')} style={{ ...secondaryButton, flex: 2 }}>
            {translator.t('search.space')}
          </button>
          <button type="button" data-f="1" onClick={erase} style={{ ...secondaryButton, flex: 1 }}>
            {translator.t('keyboard.erase')}
          </button>
          <button type="button" data-f="1" onClick={clear} style={{ ...secondaryButton, flex: 1 }}>
            {translator.t('keyboard.clear')}
          </button>
        </div>
      </div>

      {/* La colonna di destra: schede e risultati, oppure le ricerche recenti. */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          paddingBottom: 80,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        {!searching ? (
          <RecentAndHints
            recent={recent}
            onPick={(text) => setQuery(text)}
            onClearRecent={() => handlers.current.onRecentChange([])}
          />
        ) : tabs.length === 0 ? (
          <div data-search-empty="1" style={{ paddingTop: 40, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ font: `700 34px ${FONT_STACK}` }}>
              {translator.t('search.noResults', { query: query.trim() })}
            </span>
            <span style={{ font: `500 24px ${FONT_STACK}`, color: colors.textDim }}>
              {translator.t('search.noResultsHint')}
            </span>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {tabs.map((entry) => {
                const selected = entry.tab === activeTab;
                return (
                  <button
                    key={entry.tab}
                    type="button"
                    data-f="1"
                    data-search-tab={entry.tab}
                    data-selected={selected ? '1' : '0'}
                    onClick={() => setTab(entry.tab)}
                    style={{
                      flex: 'none',
                      minHeight: 56,
                      padding: '0 26px',
                      borderRadius: 28,
                      border: 0,
                      background: selected ? colors.accent : colors.surfaceRaised,
                      color: selected ? colors.background : colors.text,
                      font: `700 22px ${FONT_STACK}`,
                      cursor: 'pointer',
                    }}
                  >
                    {`${translator.t(`search.tab.${entry.tab}`)} · ${entry.count}`}
                  </button>
                );
              })}
            </div>

            {shown.tv.length > 0 && (
              <Section title={translator.t('search.section.tv')} note={translator.t('search.tvNote')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {shown.tv.map((item) => (
                    <ProgramRow
                      key={item.id}
                      program={item}
                      channel={channelsById.get(item.channelId) ?? null}
                      nowMs={nowMs}
                      hasReminder={reminderIds.includes(`${item.channelId}@${item.startMs}`)}
                      onActivate={() => activateProgram(item)}
                    />
                  ))}
                </div>
              </Section>
            )}

            {shown.channels.length > 0 && (
              <Section title={translator.t('search.section.channels')}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {shown.channels.map((item) => (
                    <ChannelCard key={item.id} channel={item} onActivate={() => openChannel(item.id)} />
                  ))}
                </div>
              </Section>
            )}

            {shown.movies.length > 0 && (
              <Section title={translator.t('search.section.movies')}>
                <PosterRow items={shown.movies} progress={progress} onOpen={openVod} />
              </Section>
            )}

            {shown.series.length > 0 && (
              <Section title={translator.t('search.section.series')}>
                <PosterRow items={shown.series} progress={progress} onOpen={openVod} />
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ font: `700 30px ${FONT_STACK}` }}>{title}</span>
        {note !== undefined && (
          <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textFaint }}>{note}</span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Le ricerche recenti, e nient'altro.
 *
 * Il prototipo qui mostra anche «Di tendenza», ordinato per popolarità. **Quel dato non esiste**: il
 * catalogo non porta un numero di visualizzazioni, e riempire quella fascia con gli ultimi arrivi
 * travestiti da tendenze sarebbe un dato inventato con un'etichetta che mente. Quando il gateway
 * porterà una popolarità, la fascia si aggiunge qui.
 */
function RecentAndHints({
  recent,
  onPick,
  onClearRecent,
}: {
  recent: ReadonlyArray<string>;
  onPick: (text: string) => void;
  onClearRecent: () => void;
}): ReactNode {
  const translator = useTranslator();
  if (recent.length === 0) {
    return (
      <div data-search-idle="1" style={{ paddingTop: 40, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span style={{ font: `700 34px ${FONT_STACK}` }}>{translator.t('search.idle')}</span>
        <span style={{ font: `500 24px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('search.idleHint')}
        </span>
      </div>
    );
  }
  return (
    <div data-search-recent="1" style={{ paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <span style={{ font: `700 30px ${FONT_STACK}` }}>{translator.t('search.recent')}</span>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {recent.map((entry) => (
          <button
            key={entry}
            type="button"
            data-f="1"
            data-recent={entry}
            onClick={() => onPick(entry)}
            style={{
              flex: 'none',
              minHeight: 56,
              padding: '0 26px',
              borderRadius: 28,
              border: `2px solid ${colors.surfaceRaised}`,
              background: colors.surfaceRaised,
              color: colors.text,
              font: `600 23px ${FONT_STACK}`,
              cursor: 'pointer',
            }}
          >
            {entry}
          </button>
        ))}
      </div>
      <button
        type="button"
        data-f="1"
        data-clear-recent="1"
        onClick={onClearRecent}
        style={{ ...secondaryButton, alignSelf: 'flex-start', padding: '0 26px' }}
      >
        {translator.t('search.clearRecent')}
      </button>
    </div>
  );
}

function ProgramRow({
  program,
  channel,
  nowMs,
  hasReminder,
  onActivate,
}: {
  program: Program;
  channel: Channel | null;
  nowMs: number;
  hasReminder: boolean;
  onActivate: () => void;
}): ReactNode {
  const translator = useTranslator();
  const live = isLive(program, nowMs);
  const time = new Date(program.startMs).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <button
      type="button"
      data-f="1"
      data-search-program={program.id}
      onClick={onActivate}
      style={{
        minHeight: 96,
        padding: '14px 24px',
        borderRadius: 14,
        border: 0,
        background: colors.surface,
        color: colors.text,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          flex: 'none',
          minWidth: 104,
          padding: '6px 12px',
          borderRadius: 8,
          textAlign: 'center',
          font: `800 18px ${FONT_STACK}`,
          letterSpacing: '.04em',
          background: live ? colors.live : hasReminder ? colors.success : colors.info,
          color: live || hasReminder ? colors.background : colors.text,
        }}
      >
        {live
          ? translator.t('search.live')
          : hasReminder
            ? `✓ ${time}`
            : time}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ font: `700 26px ${FONT_STACK}`, wordBreak: 'break-word' }}>{program.title}</span>
        <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
          {channel === null
            ? translator.t('search.unknownChannel')
            : `${channel.number} · ${channel.name}`}
        </span>
      </span>
    </button>
  );
}

function ChannelCard({ channel, onActivate }: { channel: Channel; onActivate: () => void }): ReactNode {
  return (
    <button
      type="button"
      data-f="1"
      data-search-channel={channel.id}
      onClick={onActivate}
      style={{
        flex: 'none',
        width: 300,
        minHeight: 92,
        padding: '12px 20px',
        borderRadius: 14,
        border: 0,
        background: colors.surface,
        color: colors.text,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          flex: 'none',
          width: 62,
          height: 62,
          borderRadius: 10,
          background: channelColorOf(channel.name).background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          font: `800 20px ${FONT_STACK}`,
          color: channelColorOf(channel.name).foreground,
          overflow: 'hidden',
        }}
      >
        {channel.logoUrl === null ? (
          channel.number
        ) : (
          <img
            src={channel.logoUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            font: `700 24px ${FONT_STACK}`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {channel.name}
        </span>
        <span style={{ font: `500 19px ${FONT_STACK}`, color: colors.textDim }}>{channel.number}</span>
      </span>
    </button>
  );
}

function PosterRow({
  items,
  progress,
  onOpen,
}: {
  items: ReadonlyArray<VodItem>;
  progress: WatchProgress;
  onOpen: (item: VodItem) => void;
}): ReactNode {
  const translator = useTranslator();
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {items.map((item) => {
        const state = watchStateOf(item, progress);
        return (
          <PosterTile
            key={item.id}
            title={item.title}
            subtitle={subtitleOf(item, translator)}
            posterUrl={item.posterUrl}
            percent={state.percent > 0 ? state.percent : null}
            watched={state.watched}
            lockedRating={null}
            // Il fuoco qui è quello del DOM, non un indice: lo muove `useSpatialFocus`, e il contorno
            // arriva dalla regola globale. Vedi il commento dentro `PosterTile`.
            focused={false}
            onSelect={() => onOpen(item)}
          />
        );
      })}
    </div>
  );
}

/** «Film · Azione · 2024», con le parti che mancano semplicemente assenti. */
function subtitleOf(item: VodItem, translator: { t: (key: string) => string }): string {
  const parts: string[] = [translator.t(item.kind === 'series' ? 'vod.kind.series' : 'vod.kind.movie')];
  if (item.genre.trim() !== '') parts.push(item.genre);
  // Zero non è un anno: l'importatore lo scrive quando la sorgente non lo manda.
  if (item.year > 0) parts.push(String(item.year));
  return parts.join(' · ');
}

const secondaryButton = {
  minHeight: 64,
  padding: '0 18px',
  borderRadius: 12,
  border: 0,
  background: colors.surfaceButton,
  color: colors.text,
  font: `700 22px ${FONT_STACK}`,
  cursor: 'pointer',
} as const;
